import React, { useEffect, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { Sparkles, RefreshCcw, Clock } from "lucide-react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";

const formatGeneratedAt = (iso) => {
  if (!iso) return "";
  const t = new Date(iso);
  const diffMin = Math.round((Date.now() - t.getTime()) / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffMin < 1440) return `${Math.round(diffMin / 60)}h ago`;
  return t.toLocaleString();
};

const DailyBriefing = () => {
  const { user } = useAuth();
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const { data } = await client.post(`/ai/daily-briefing${force ? "?force=true" : ""}`);
      setBriefing(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to generate briefing");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [user?.user_id]);

  return (
    <section data-testid="daily-briefing" className="relative">
      <div className="glass-strong p-6 lg:p-10 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{ backgroundImage: "radial-gradient(circle at 85% 10%, rgba(59,130,246,0.35), transparent 50%), radial-gradient(circle at 10% 90%, rgba(16,185,129,0.20), transparent 55%)" }}
        />
        <div className="relative">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/15 flex items-center justify-center">
                <Sparkles size={16} className="text-blue-400" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-400">Daily AI Briefing</div>
                <div className="font-display text-base mt-1 text-neutral-200">
                  {briefing?.personalized ? "Personalized to your watchlist" : "Today's market overview"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {briefing?.generated_at && (
                <div className="hidden md:flex items-center gap-1.5 text-xs text-neutral-400">
                  <Clock size={11} />
                  <span>{formatGeneratedAt(briefing.generated_at)}</span>
                </div>
              )}
              <button
                data-testid="briefing-refresh"
                onClick={() => load(true)}
                disabled={refreshing}
                className="btn-glass !py-2 !px-3 text-xs flex items-center gap-2"
              >
                <RefreshCcw size={12} className={refreshing ? "animate-spin" : ""} />
                <span>{refreshing ? "Updating…" : "Refresh"}</span>
              </button>
            </div>
          </div>

          {loading && (
            <div className="space-y-3" data-testid="briefing-loading">
              <div className="h-7 w-2/3 bg-white/5 rounded animate-pulse" />
              <div className="h-4 w-full bg-white/5 rounded animate-pulse" />
              <div className="h-4 w-5/6 bg-white/5 rounded animate-pulse" />
              <div className="h-4 w-4/6 bg-white/5 rounded animate-pulse" />
              <div className="text-xs text-neutral-500 pt-3">Claude is reading the markets…</div>
            </div>
          )}

          {error && !loading && (
            <div className="text-rose-400 text-sm" data-testid="briefing-error">
              Couldn't generate today's briefing: {error}.
              <button onClick={() => load(true)} className="underline ml-2">Try again</button>
            </div>
          )}

          {!loading && !error && briefing?.content && (
            <div className="briefing-md max-w-3xl">
              <ReactMarkdown
                components={{
                  h1: ({ node, ...p }) => <h2 className="font-display text-3xl sm:text-4xl tracking-tighter mb-4 text-white" {...p} />,
                  h2: ({ node, ...p }) => <h3 className="font-display text-base uppercase tracking-widest text-neutral-400 mt-7 mb-3" {...p} />,
                  h3: ({ node, ...p }) => <h4 className="font-display text-lg mt-5 mb-2 text-white" {...p} />,
                  p: ({ node, ...p }) => <p className="text-neutral-200 leading-relaxed mb-3 text-sm sm:text-base" {...p} />,
                  ul: ({ node, ...p }) => <ul className="space-y-1.5 mb-4 list-disc pl-5 text-neutral-200 text-sm sm:text-base" {...p} />,
                  li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
                  strong: ({ node, ...p }) => <strong className="text-white font-semibold" {...p} />,
                  em: ({ node, ...p }) => <em className="text-neutral-500 not-italic block mt-5 text-xs" {...p} />,
                }}
              >
                {briefing.content}
              </ReactMarkdown>
              {briefing.assets?.length > 0 && (
                <div className="mt-6 pt-5 border-t border-white/5 flex flex-wrap items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500">Covered today:</span>
                  {briefing.assets.map((a) => (
                    <span key={`${a.kind}-${a.symbol}`} data-testid={`briefing-asset-${a.symbol}`} className="chip !py-0.5 !text-[10px] !uppercase">
                      {a.symbol}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {!loading && !user && !error && (
            <div className="mt-5 text-xs text-neutral-400">
              <span className="text-neutral-500">Tip:</span> Sign in and add assets to your watchlist to get a fully personalized briefing every morning.
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DailyBriefing;
